import { useLayoutEffect, useState, useRef } from "react";

interface UseModalBodyPaddingProps {
  isMobile: boolean;
  dependencies?: unknown[];
}

/**
 * Hook to calculate dynamic padding for a mobile modal body
 * based on header, footer, and content heights.
 *
 * Calculates the padding needed so the last body element
 * is visible when scrolling, without excessive space.
 *
 * @param isMobile - If true, calculates padding. If false, returns 0.
 * @param dependencies - Additional dependencies to recalculate (optional)
 * @returns Object with the calculated padding in pixels and refs to attach to the DOM elements
 */
export const useModalBodyPadding = ({
  isMobile,
  dependencies = [],
}: UseModalBodyPaddingProps) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [bodyPadding, setBodyPadding] = useState<number>(0);
  const lastPaddingRef = useRef<number>(0);
  const lastCalculationTimeRef = useRef<number>(0);
  const ignoreResizeUntilRef = useRef<number>(0);
  const stableHeightRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    if (!isMobile) {
      setBodyPadding(0);
      lastPaddingRef.current = 0;
      stableHeightRef.current = null;
      return;
    }

    const calculatePadding = () => {
      // Ignore resize events for a short period after applying padding
      // to prevent infinite loops
      const now = Date.now();
      if (now < ignoreResizeUntilRef.current) {
        return;
      }

      if (
        !headerRef.current ||
        !bodyRef.current ||
        !footerRef.current ||
        !containerRef.current
      ) {
        return;
      }

      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        try {
          // 1. Get viewport height (total visible screen height)
          const viewportHeight = window.innerHeight;

          // 2. Measure element heights using getBoundingClientRect
          const headerRect = headerRef.current!.getBoundingClientRect();
          const headerHeight = headerRect.height;

          // Measure footer: first try parentElement (ModalFooter),
          // if it has no height, measure the internal div directly
          const footerElement = footerRef.current!.parentElement;
          let footerHeight = 0;
          let isFooterFixed = false;

          if (footerElement) {
            const footerComputedStyle = window.getComputedStyle(footerElement);
            isFooterFixed = footerComputedStyle.position === "fixed";

            const footerRect = footerElement.getBoundingClientRect();
            footerHeight = footerRect.height;

            // If ModalFooter has height 0 or very small, measure the internal div
            if (footerHeight < 10 && footerRef.current) {
              const innerFooterRect = footerRef.current.getBoundingClientRect();
              footerHeight = innerFooterRect.height;
            }
          } else if (footerRef.current) {
            // Fallback: measure the internal div directly
            const innerFooterRect = footerRef.current.getBoundingClientRect();
            footerHeight = innerFooterRect.height;
          }

          // 3. Calculate visible body height
          // If footer is fixed, it doesn't take space in the flow
          const bodyVisibleHeight = isFooterFixed
            ? viewportHeight - headerHeight // Footer is fixed, doesn't reduce visible space
            : viewportHeight - headerHeight - footerHeight; // Footer is in flow

          // 4. Measure complete body content height
          // CRITICAL: We need to measure the content WITHOUT the padding we applied
          // Get the computed style to check current padding
          const container = containerRef.current!;
          const computedStyle = window.getComputedStyle(container);
          const currentPaddingBottom = parseFloat(
            computedStyle.paddingBottom || "0"
          );

          // Get scrollHeight, but subtract our current padding if it exists
          // to get the "natural" content height
          const scrollHeight = container.scrollHeight;
          const naturalContentHeight =
            scrollHeight -
            (currentPaddingBottom > 0 ? currentPaddingBottom : 0);

          // 5. Calculate needed padding
          const additionalSpacing = 16; // 1rem = 16px
          let finalPadding = 0;

          if (isFooterFixed) {
            // When footer is fixed, we need padding to ensure:
            // 1. Content in container is not hidden behind the fixed footer
            // 2. User can scroll to see all content including what's in the footer

            // Minimum padding: footer height + spacing
            let minPadding = footerHeight + additionalSpacing;

            // If container content is taller than visible area, we need extra padding
            // to allow scrolling to the end of the container content
            if (naturalContentHeight > bodyVisibleHeight) {
              const extraPaddingNeeded =
                naturalContentHeight - bodyVisibleHeight;
              // Padding needed = footer height (so footer doesn't cover content) +
              // extra space to scroll to bottom of container content + spacing
              finalPadding =
                footerHeight + extraPaddingNeeded + additionalSpacing;
            } else {
              // Content fits in visible area, just need padding for footer
              finalPadding = minPadding;
            }
          } else {
            // Footer is in flow, use original calculation
            const paddingNeeded = naturalContentHeight - bodyVisibleHeight;
            finalPadding =
              paddingNeeded > 0
                ? paddingNeeded + additionalSpacing
                : additionalSpacing;
          }

          // Safety check: prevent unreasonably large padding values
          // If padding is more than 1.5x the viewport height, something is wrong
          const maxReasonablePadding = viewportHeight * 1.5;
          if (finalPadding > maxReasonablePadding) {
            // If we get an unreasonable value, use a stable height if we have one
            if (stableHeightRef.current !== null) {
              const stablePadding =
                stableHeightRef.current - bodyVisibleHeight + additionalSpacing;
              if (stablePadding > 0 && stablePadding <= maxReasonablePadding) {
                finalPadding = stablePadding;
              } else {
                finalPadding = additionalSpacing;
              }
            } else {
              finalPadding = additionalSpacing;
            }
          } else {
            // Store stable height for future reference
            stableHeightRef.current = naturalContentHeight;
          }

          setBodyPadding((prevPadding) => {
            // Only update if padding changed significantly (more than 5px difference)
            // and the new value is reasonable
            const paddingDiff = Math.abs(prevPadding - finalPadding);
            if (
              paddingDiff > 5 &&
              finalPadding <= maxReasonablePadding &&
              finalPadding >= 0
            ) {
              lastPaddingRef.current = finalPadding;
              lastCalculationTimeRef.current = Date.now();
              // Ignore resize events for 300ms after applying new padding
              ignoreResizeUntilRef.current = Date.now() + 300;
              return finalPadding;
            }
            return prevPadding;
          });
        } catch (error) {
          console.error("Error calculating body padding:", error);
        }
      });
    };

    // Throttle function to prevent excessive calculations
    let throttleTimer: NodeJS.Timeout | null = null;
    let lastThrottleCall = 0;
    const throttledCalculate = () => {
      const now = Date.now();
      const timeSinceLastCall = now - lastThrottleCall;

      if (throttleTimer) {
        clearTimeout(throttleTimer);
      }

      // If called recently, debounce it
      if (timeSinceLastCall < 100) {
        throttleTimer = setTimeout(() => {
          lastThrottleCall = Date.now();
          calculatePadding();
        }, 100 - timeSinceLastCall);
      } else {
        lastThrottleCall = now;
        calculatePadding();
      }
    };

    // Measure initially after DOM has been rendered
    const timeoutId = setTimeout(calculatePadding, 150);

    // Use ResizeObserver to detect changes in element sizes
    // IMPORTANT: Do NOT observe containerRef to avoid infinite loop
    const resizeObserver = new ResizeObserver((entries) => {
      // Check if the resize is from an element we care about
      const relevantEntry = entries.find((entry) => {
        const target = entry.target;
        return (
          target === headerRef.current ||
          target === bodyRef.current ||
          target === footerRef.current ||
          target === footerRef.current?.parentElement
        );
      });

      if (relevantEntry) {
        throttledCalculate();
      }
    });

    // Observe relevant elements (but NOT the container we're modifying)
    if (headerRef.current) resizeObserver.observe(headerRef.current);
    if (bodyRef.current) resizeObserver.observe(bodyRef.current);
    if (footerRef.current) resizeObserver.observe(footerRef.current);
    // DO NOT observe containerRef.current - it causes infinite loop

    // Also observe the footer parent (ModalFooter)
    const footerParent = footerRef.current?.parentElement;
    if (footerParent) resizeObserver.observe(footerParent);

    // Observe viewport changes with throttle
    window.addEventListener("resize", throttledCalculate, { passive: true });

    return () => {
      resizeObserver.disconnect();
      clearTimeout(timeoutId);
      if (throttleTimer) {
        clearTimeout(throttleTimer);
      }
      window.removeEventListener("resize", throttledCalculate);
      lastPaddingRef.current = 0;
      stableHeightRef.current = null;
      ignoreResizeUntilRef.current = 0;
    };
  }, [isMobile, headerRef, bodyRef, footerRef, containerRef, ...dependencies]);

  return { bodyPadding, headerRef, bodyRef, footerRef, containerRef };
};
